import { useContext, useMemo } from 'react';
import type { SignalIncident } from 'src/graphql/entities/signal-incident/signal-incident.types';
import {
  ChildStatus,
  MessageTypeId,
  MessageTypeRegistration,
  TimelineEntry,
} from 'src/pages/Conversations/Chat/registry/types';
import {
  DEFAULT_TIMEOUT_MS,
  useReadinessWithTimeout,
} from 'src/pages/Conversations/Chat/registry/use-readiness-with-timeout';
import { SignalIncidentContext } from './signal-incident-context';
import { SignalIncidentProvider } from './signal-incident-provider';
import { SignalIncidentRow } from './signal-incident-row';

function useItems(): TimelineEntry<SignalIncident>[] {
  const context = useContext(SignalIncidentContext);
  const incidents = context?.incidents;

  return useMemo<TimelineEntry<SignalIncident>[]>(
    () =>
      (incidents ?? []).map((incident) => ({
        type: MessageTypeId.SignalIncident,
        timestamp: incident.createdAt,
        item: incident,
      })),
    [incidents],
  );
}

function useReadiness(): ChildStatus {
  const context = useContext(SignalIncidentContext);
  const incidents = context?.incidents ?? [];
  const isLoading = context?.isLoading ?? false;
  const error = context?.error;

  return useReadinessWithTimeout({
    loading: isLoading,
    error,
    hasData: incidents.length > 0,
    timeoutMs: DEFAULT_TIMEOUT_MS,
  });
}

export const signalIncidentRegistration: MessageTypeRegistration<SignalIncident> = {
  id: MessageTypeId.SignalIncident,
  Row: SignalIncidentRow,
  Provider: SignalIncidentProvider,
  getKey: (item) => item.signalIncidentId,
  useItems,
  useReadiness,
  blockingForInitialRender: true,
  timeoutMs: DEFAULT_TIMEOUT_MS,
};

export default signalIncidentRegistration;
